import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, ArrowLeft } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { plainJP } from "@/components/JP";
import { F, Sel, inputCls, btnPrimary, btnGhost, LEVELS, AREAS } from "@/components/admin/ui";

const EMPTY = { title_jp: "", title_id: "", level: "N4", area: "bunpo", plan: "", code: "", semester: "", kind: "lesson", competency: "", summary_id: "", duration_min: 10, blocks: [{ jp: "", id: "" }], vocab: [], practice_ids: [], test_ids: [] };

function PairList({ label, items, onChange, testid, placeholderJp, placeholderId, rows = 2 }) {
  const upd = (i, k, v) => onChange(items.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  return (
    <div data-testid={testid}>
      <div className="mb-1 flex items-center justify-between"><span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span><button type="button" data-testid={`${testid}-add`} onClick={() => onChange([...items, { jp: "", id: "" }])} className={btnGhost}><Plus className="h-3 w-3" />Tambah</button></div>
      <div className="space-y-2">
        {items.map((b, i) => (
          <div key={i} className="grid gap-2 rounded-xl bg-muted/50 p-2 sm:grid-cols-[1fr_1fr_32px]">
            <textarea data-testid={`${testid}-jp-${i}`} rows={rows} className={inputCls} value={b.jp} onChange={(e) => upd(i, "jp", e.target.value)} placeholder={placeholderJp} />
            <textarea data-testid={`${testid}-id-${i}`} rows={rows} className={inputCls} value={b.id} onChange={(e) => upd(i, "id", e.target.value)} placeholder={placeholderId} />
            <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => onChange(items.filter((_, j) => j !== i))} className="self-center text-muted-foreground hover:text-primary"><X className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuestionPicker({ label, questions, selected, onChange, testid }) {
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div data-testid={testid}>
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label} ({selected.length})</span>
      <div className="max-h-48 space-y-1 overflow-y-auto rounded-xl border border-border p-2">
        {questions.map((q) => (
          <label key={q.id} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-xs hover:bg-accent">
            <input type="checkbox" data-testid={`${testid}-${q.id}`} checked={selected.includes(q.id)} onChange={() => toggle(q.id)} className="accent-[hsl(var(--primary))]" />
            <span className="text-muted-foreground">{q.type}/{q.level}</span><span lang="ja" className="jp truncate">{plainJP(q.prompt_jp)}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function ModuleForm({ initial, questions, onSaved, onCancel }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f, plan: f.plan || f.level, code: f.code || null, semester: f.semester || null, duration_min: Number(f.duration_min), blocks: f.blocks.filter((b) => b.jp.trim()), vocab: f.vocab.filter((v) => v.jp.trim()) };
    try {
      if (f.id) await api.put(`/admin/modules/${f.id}`, body); else await api.post("/admin/modules", body);
      toast.success("Modul disimpan"); onSaved();
    } catch (ex) { toast.error(errMsg(ex)); }
  };
  return (
    <form onSubmit={submit} className="soft-card space-y-4 p-5" data-testid="module-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <F label="Judul (JP)"><input data-testid="module-title-jp-input" required className={inputCls} value={f.title_jp} onChange={set("title_jp")} /></F>
        <F label="Judul (ID)"><input data-testid="module-title-id-input" required className={inputCls} value={f.title_id} onChange={set("title_id")} /></F>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <F label="Level"><Sel testid="module-level-select" value={f.level} onChange={(v) => setF({ ...f, level: v })} options={LEVELS} /></F>
        <F label="Area"><Sel testid="module-area-select" value={f.area} onChange={(v) => setF({ ...f, area: v })} options={AREAS} /></F>
        <F label="Durasi (menit)"><input data-testid="module-duration-input" type="number" min={1} className={inputCls} value={f.duration_min} onChange={set("duration_min")} /></F>
      </div>
      <F label="Ringkasan (ID)"><input data-testid="module-summary-input" className={inputCls} value={f.summary_id} onChange={set("summary_id")} /></F>
      <div className="grid gap-3 sm:grid-cols-4">
        <F label="Plan"><Sel testid="module-plan-select" value={f.plan || f.level} onChange={(v) => setF({ ...f, plan: v })} options={LEVELS} /></F>
        <F label="Kode (mis. N4-14)"><input data-testid="module-code-input" className={inputCls} value={f.code || ""} onChange={set("code")} /></F>
        <F label="Semester (opsional)"><input data-testid="module-semester-input" className={inputCls} value={f.semester || ""} onChange={set("semester")} placeholder="N4-A" /></F>
        <F label="Jenis"><Sel testid="module-kind-select" value={f.kind || "lesson"} onChange={(v) => setF({ ...f, kind: v })} options={["lesson", "review", "exam"]} /></F>
      </div>
      <F label="Kompetensi yang dikuasai"><input data-testid="module-competency-input" className={inputCls} value={f.competency || ""} onChange={set("competency")} /></F>
      <p className="text-xs text-muted-foreground">Jika soal latihan / mini test tidak dipilih, sistem otomatis memilih soal dari bank sesuai level & area modul.</p>
      <PairList label="Blok bacaan (JP | ID) — furigana: [漢字|かんじ|N4]" items={f.blocks} onChange={(b) => setF({ ...f, blocks: b })} testid="module-blocks" placeholderJp="日本語の本文" placeholderId="Terjemahan Indonesia" rows={3} />
      <PairList label="Kosakata (JP | arti ID)" items={f.vocab} onChange={(v) => setF({ ...f, vocab: v })} testid="module-vocab" placeholderJp="[単語|たんご|N4]" placeholderId="kosakata" rows={1} />
      <div className="grid gap-3 sm:grid-cols-2">
        <QuestionPicker label="Soal latihan" questions={questions} selected={f.practice_ids} onChange={(v) => setF({ ...f, practice_ids: v })} testid="module-practice-picker" />
        <QuestionPicker label="Soal mini test" questions={questions} selected={f.test_ids} onChange={(v) => setF({ ...f, test_ids: v })} testid="module-test-picker" />
      </div>
      <div className="flex gap-2"><button data-testid="module-save-button" className={btnPrimary}>Simpan modul</button><button type="button" data-testid="module-cancel-button" onClick={onCancel} className={btnGhost}>Batal</button></div>
    </form>
  );
}

export function ModulesAdmin({ focus = null, onBack = null }) {
  const [list, setList] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => Promise.all([api.get("/admin/modules"), api.get("/admin/questions")]).then(([m, q]) => { setList(m.data); setQuestions(q.data); });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!focus?.id || !list.length) return;
    setTimeout(() => document.getElementById(`mrow-${focus.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
  }, [focus, list]);
  const remove = async (m) => {
    if (!window.confirm(`Hapus modul "${m.title_id}"?`)) return;
    await api.delete(`/admin/modules/${m.id}`); toast.success("Modul dihapus"); load();
  };
  return (
    <div className="space-y-5" data-testid="admin-modules">
      {onBack && !editing && <button data-testid="module-back-insight" onClick={onBack} className={btnGhost}><ArrowLeft className="h-3.5 w-3.5" />Kembali ke Insight{focus?.id ? ` · modul ${focus.id}` : ""}</button>}
      {editing ? <ModuleForm initial={editing} questions={questions} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
        : <div className="flex justify-end"><button data-testid="module-new-button" onClick={() => setEditing(EMPTY)} className={btnPrimary}><Plus className="h-4 w-4" />Modul baru</button></div>}
      <div className="space-y-2">
        {list.map((m) => (
          <div key={m.id} id={`mrow-${m.id}`} data-testid={`module-row-${m.id}`} className={`soft-card flex items-start justify-between gap-3 px-5 py-3.5 ${focus?.id === m.id ? "ring-2 ring-primary" : ""}`}>
            <div className="min-w-0">
              <p className="text-[11px] text-muted-foreground">{m.code ? `${m.code} · ` : `#${m.order} · `}{m.plan || m.level}{m.semester ? ` · ${m.semester}` : ""} · {m.area} · {m.kind || "lesson"} · {m.blocks.length} blok · latihan {m.practice_ids.length || "auto"} · test {m.test_ids.length || "auto"}</p>
              <p lang="ja" className="jp truncate text-sm font-medium">{plainJP(m.title_jp)}</p>
              <p className="truncate text-xs text-muted-foreground">{m.title_id}</p>
            </div>
            <div className="flex shrink-0 gap-1.5">
              <button data-testid={`module-edit-${m.id}`} onClick={() => setEditing({ ...EMPTY, ...m })} className={btnGhost}><Pencil className="h-3.5 w-3.5" /></button>
              <button data-testid={`module-delete-${m.id}`} onClick={() => remove(m)} className={`${btnGhost} text-primary`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
